// __ROUTE_DRAFTS__ (2026-10-03) Finalize a draft route: pick who gets the whole
// route (or keep it Unassigned), see exactly which jobs move, confirm.
import React, { useEffect, useState } from 'react';
import { EquipmentIssueList } from '../EquipmentGate';   // __EQUIP_COMPAT__

const OPEN = '__open__';

const listNames = (loads, nameOf) => loads.map((l) => '#' + (l.load_number || String(l.id).slice(0, 8))
  + (nameOf && l.driver_id ? ' (' + nameOf(l.driver_id) + ')' : '')
  + (l.status ? ' - ' + l.status : '')).join(', ');

export default function FinalizeDialog({ draft, drivers, getDriverName, onPreview, onConfirm, onCancel, onQualify }) {
  const [target, setTarget] = useState('');
  const [date, setDate] = useState(draft.route_date || '');
  const [preview, setPreview] = useState(null);
  const [ack, setAck] = useState(false);
  const [working, setWorking] = useState(false);
  const [err, setErr] = useState(null);
  // __EQUIP_V2__ Henry: "once it's built and I'm ready to assign it, there should be a way to
  // show who qualifies vs who doesn't, and still allow me to assign to a non-compatible driver
  // but warn me of the incompatibilities so I can see what and where to compensate."
  // Every driver checked against the whole route on the route day: equipment (which job,
  // which limit, by how much), temporary restrictions, half-load season, and his day / HOS.
  const [qual, setQual] = useState(null);
  const [qualErr, setQualErr] = useState(null);
  const [openRow, setOpenRow] = useState(null);
  useEffect(() => {
    let alive = true;
    setQual(null); setQualErr(null);
    if (!date || !onQualify) return undefined;
    onQualify(date).then((r) => { if (alive) setQual(r); }).catch((e) => { if (alive) setQualErr(e.message || String(e)); });
    return () => { alive = false; };
  }, [date]); // eslint-disable-line react-hooks/exhaustive-deps
  const equip = qual && target && target !== OPEN
    ? ([...qual.qualifies, ...qual.notQualified].find((x) => x.driver.id === target) || {}).result : null;
  const driverRow = (x) => {
    const r = x.result;
    const n = r.stops.length + r.blocking.length + r.warnings.length;
    const on = target === x.driver.id;
    return (
      <div key={x.driver.id} style={{ padding: '0.4rem 0.5rem', marginBottom: '0.3rem', borderRadius: '4px', cursor: 'pointer',
        border: '1px solid ' + (on ? 'var(--accent-primary)' : 'var(--border-default)'), backgroundColor: on ? 'var(--surface-3)' : 'var(--surface-1)' }}
        onClick={() => { setTarget(x.driver.id); setOpenRow(x.driver.id); }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.5rem', fontSize: '0.84rem' }}>
          <span><input type="radio" readOnly checked={on} style={{ marginRight: '0.35rem' }} /><strong>{x.driver.name || x.driver.email || 'Driver'}</strong>
            <span style={{ color: 'var(--text-muted)' }}> - {r.unit ? r.unit.label : 'no unit'}</span></span>
          <span style={{ color: r.ok ? 'var(--color-success)' : (r.canAssign && !r.needsReason ? 'var(--color-warning)' : 'var(--color-danger)'), whiteSpace: 'nowrap' }}>
            {r.ok ? 'qualifies' : (!r.canAssign ? 'cannot' : n + ' issue' + (n === 1 ? '' : 's'))}
          </span>
        </div>
        {r.timingNote && <div style={{ fontSize: '0.74rem', color: 'var(--text-muted)', marginLeft: '1.3rem' }}>{r.timingNote}</div>}
        {(openRow === x.driver.id || on) && !r.ok && <div style={{ marginLeft: '1.3rem', marginTop: '0.2rem' }}><EquipmentIssueList result={r} compact hideInfo /></div>}
      </div>
    );
  };

  useEffect(() => {
    let alive = true;
    setPreview(null); setAck(false); setErr(null);
    if (!target || !date) return undefined;
    onPreview(target === OPEN ? null : target, date)
      .then((p) => { if (alive) setPreview(p); })
      .catch((e) => { if (alive) setErr(e.message || String(e)); });
    return () => { alive = false; };
  }, [target, date]); // eslint-disable-line react-hooks/exhaustive-deps

  const plan = preview && preview.plan;
  const moving = plan ? plan.ok.length + plan.assigned.length + plan.inProgress.length + plan.stay.length : 0;
  const canGo = !!plan && !!date && moving > 0 && (!plan.needsConfirm || ack) && !working;

  const go = async () => {
    setWorking(true); setErr(null);
    try {
      await onConfirm({ targetDriverId: target === OPEN ? null : target, date, confirmed: !!(plan && plan.needsConfirm && ack) });
    } catch (e) {
      setErr(e.message || String(e));
      setWorking(false);
    }
  };

  const box = { backgroundColor: 'var(--surface-2)', padding: '0.6rem', borderRadius: '5px', marginBottom: '0.6rem', fontSize: '0.82rem' };
  return (
    <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100 }}>
      <div style={{ backgroundColor: 'var(--surface-1)', borderRadius: 'var(--radius-lg)', padding: '1.25rem', width: '92%', maxWidth: '520px', maxHeight: '85vh', overflow: 'auto', border: '1px solid var(--border-default)' }}>
        <h3 style={{ marginTop: 0, color: 'var(--accent-primary)' }}>Finalize "{draft.name}"</h3>
        <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginTop: 0 }}>
          This makes the draft real: the jobs leave their current routes and the whole route goes to the driver you pick, in the draft's order.
        </p>
        <label style={{ display: 'block', fontSize: '0.8rem', marginBottom: '0.25rem' }}>Route day</label>
        <input type="date" value={date} onChange={(e) => setDate(e.target.value)} style={{ padding: '0.4rem', marginBottom: '0.6rem', backgroundColor: 'var(--bg-primary)', color: 'var(--text-primary)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-sm)' }} />
        <label style={{ display: 'block', fontSize: '0.8rem', marginBottom: '0.25rem' }}>Assign the whole route to</label>
        {!onQualify && (
          <select value={target} onChange={(e) => setTarget(e.target.value)} style={{ width: '100%', padding: '0.45rem', marginBottom: '0.6rem', backgroundColor: 'var(--bg-primary)', color: 'var(--text-primary)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-sm)' }}>
            <option value="">Choose...</option>
            {drivers.map((d) => <option key={d.id} value={d.id}>{d.name || d.email || 'Driver'}</option>)}
            <option value={OPEN}>Unassigned (keep it as an open route for now)</option>
          </select>
        )}
        {onQualify && (
          <div data-qualifies-list style={{ maxHeight: '38vh', overflow: 'auto', marginBottom: '0.6rem' }}>
            {!date && <div style={box}>Pick the route day first - the check uses that day's limits and each driver's day.</div>}
            {date && !qual && !qualErr && <div style={box}>Checking every driver against this route...</div>}
            {qualErr && <div style={{ ...box, color: 'var(--color-danger)' }}>Could not check the drivers: {qualErr}</div>}
            {qual && (
              <>
                <div style={{ fontSize: '0.78rem', fontWeight: 'bold', color: 'var(--color-success)', margin: '0.2rem 0' }}>Qualifies ({qual.qualifies.length})</div>
                {qual.qualifies.map(driverRow)}
                {!qual.qualifies.length && <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginBottom: '0.3rem' }}>Nobody fits this route as it is.</div>}
                <div style={{ fontSize: '0.78rem', fontWeight: 'bold', color: 'var(--color-warning)', margin: '0.5rem 0 0.2rem' }}>Doesn't qualify ({qual.notQualified.length}) - you can still pick one; click a name for what to compensate</div>
                {qual.notQualified.map(driverRow)}
              </>
            )}
            <div onClick={() => setTarget(OPEN)} style={{ padding: '0.4rem 0.5rem', borderRadius: '4px', cursor: 'pointer', fontSize: '0.84rem',
              border: '1px solid ' + (target === OPEN ? 'var(--accent-primary)' : 'var(--border-default)') }}>
              <input type="radio" readOnly checked={target === OPEN} style={{ marginRight: '0.35rem' }} />Unassigned (keep it as an open route for now)
            </div>
          </div>
        )}

        {err && <div style={{ ...box, color: 'var(--color-danger)' }}>{err}</div>}
        {target && target !== OPEN && equip && (
          <div style={{ ...box, borderLeft: '4px solid ' + (!equip.canAssign || equip.needsReason ? 'var(--color-danger)' : !equip.ok ? 'var(--color-warning)' : 'var(--color-success)') }}>
            <strong>{getDriverName(target) || 'Driver'}{!equip.ok ? ' - Finalize shows these warnings once more before anything changes' : ' qualifies'}:</strong>
            <EquipmentIssueList result={equip} compact />
          </div>
        )}
        {target && date && !plan && !err && <div style={box}>Checking every job's live status...</div>}
        {plan && (
          <>
            <div style={box}>
              <strong>{moving}</strong> job(s) will be on this route
              {target === OPEN ? ' (unassigned - no driver sees it until you assign it).' : ' for ' + (getDriverName(target) || 'this driver') + '.'}
              {plan.ok.length > 0 && <div style={{ marginTop: '0.3rem' }}>Free to move: {plan.ok.length}</div>}
              {plan.stay.length > 0 && <div>Already {target === OPEN ? 'unassigned' : 'this driver\'s'}: {plan.stay.length}</div>}
            </div>
            {plan.assigned.length > 0 && (
              <div style={{ ...box, borderLeft: '4px solid var(--color-warning)' }}>
                <strong>Taken off another driver:</strong> {listNames(plan.assigned, getDriverName)}.
                <div style={{ color: 'var(--text-muted)', marginTop: '0.25rem' }}>Their phone shows "Job taken off you" on its next sync.</div>
              </div>
            )}
            {plan.inProgress.length > 0 && (
              <div style={{ ...box, borderLeft: '4px solid var(--color-danger)', color: 'var(--color-danger)' }}>
                <strong>IN PROGRESS - a driver has started these:</strong> {listNames(plan.inProgress, getDriverName)}.
              </div>
            )}
            {plan.blocked.length > 0 && (
              <div style={{ ...box, color: 'var(--text-muted)' }}>Finished, will NOT move: {listNames(plan.blocked)}.</div>
            )}
            {plan.dateChanges.length > 0 && (
              <div style={{ ...box, borderLeft: '4px solid var(--color-warning)' }}>
                Booked for another day, will move to {date}: {listNames(plan.dateChanges)}.
              </div>
            )}
            {plan.needsConfirm && (
              <label style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-start', fontSize: '0.85rem', marginBottom: '0.6rem' }}>
                <input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} />
                <span>Yes, move the job(s) above off their current driver{plan.inProgress.length ? ' - including the ones already in progress' : ''}.</span>
              </label>
            )}
          </>
        )}
        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
          <button onClick={onCancel} disabled={working} style={{ padding: '0.5rem 1rem', backgroundColor: '#7f8c8d', color: 'var(--text-primary)', border: 'none', borderRadius: '5px', cursor: 'pointer' }}>Cancel</button>
          <button onClick={go} disabled={!canGo} style={{ padding: '0.5rem 1rem', backgroundColor: canGo ? 'var(--color-success)' : 'var(--btn-secondary-bg)', color: 'var(--text-primary)', border: 'none', borderRadius: '5px', cursor: canGo ? 'pointer' : 'not-allowed', fontWeight: 'bold' }}>
            {working ? 'Finalizing...' : 'Finalize route'}
          </button>
        </div>
      </div>
    </div>
  );
}
